import type { CityConfig } from "../city-template/types";

export const smyrnaDeConfig: CityConfig = {
  city: "Smyrna",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "smyrna-de",
  route: "/smyrna-de",
  title: "Emergency Passport Smyrna DE | Expedited Passport Central Delaware Kent County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Smyrna, DE — as fast as 24 hours. Fast passport renewal for central Delaware Kent County residents between Dover and Wilmington. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Smyrna, DE",
  heroSubtitle: "Living in central Delaware between Dover and Wilmington and need a passport fast? Fast Passport Center helps Smyrna, Clayton, Kenton, and Kent County residents get their expedited passports as quickly as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Smyrna%20Delaware%20historic%20downtown%20area%20with%20classic%20American%20small-town%20charm%2C%20tree-lined%20streets%20with%20historic%20houses%20and%20green%20lawns%2C%20DE%20Route%201%20corridor%20visible%20in%20the%20background%2C%20peaceful%20suburban%20atmosphere%20with%20open%20Delaware%20farmland%20on%20the%20horizon%2C%20warm%20golden%20afternoon%20light%2C%20no%20visible%20text%20or%20people%2C%20photorealistic%20landscape%20photography%20style&width=1800&height=700&seq=smyrna-de-hero-001&orientation=landscape",
  heroImageAlt: "Smyrna Delaware historic downtown with tree-lined streets and classic small-town charm",
  geoPosition: "39.2996;-75.6047",
  geoPlacename: "Smyrna, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "600 Arch St Suite 4200, Philadelphia, PA 19106",
    distance: "Approximately 70–80 minutes from Smyrna, DE via DE Route 1 and I-95 N",
  },
  nearbyCitiesZips: [
    { name: "Smyrna", zips: "19977" },
    { name: "Clayton", zips: "19938" },
    { name: "Kenton", zips: "19955" },
    { name: "Hartly", zips: "19953" },
    { name: "Cheswold", zips: "19936" },
    { name: "Leipsic", zips: "19901" },
    { name: "Camden", zips: "19934" },
    { name: "Wyoming", zips: "19934" },
    { name: "Dover", zips: "19901, 19902, 19904" },
    { name: "Middletown", zips: "19709, 19734" },
    { name: "Townsend", zips: "19734" },
    { name: "Milford", zips: "19963" },
    { name: "Frederica", zips: "19946" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Smyrna and central Delaware departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-road-map-line", title: "Central Delaware Specialist", desc: "Serving Smyrna, Clayton, Kenton, and Kent County between Dover and Wilmington" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no 70–80 minute drive to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wilmington, DE", route: "/wilmington-de" },
    { name: "Emergency passport help in Dover, DE", route: "/dover-de" },
    { name: "Urgent passport services in Newark, DE", route: "/newark-de" },
    { name: "Delaware passport services overview", route: "/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Smyrna, Delaware remotely via secure registered courier. We do not have a walk-in office in Smyrna — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Smyrna and central Delaware travelers without the long drive to Philadelphia.",
  fpcOfficeAddress: "1700 Market St, Suite 1005, Philadelphia, PA 19103",
  fpcOfficeDistance: "Serving Smyrna, Clayton, Kenton, and central Kent County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Smyrna, Delaware area and central Kent County. From the DE Route 1 corridor to the rural communities along the Lebanon and St. Jones River, from Clayton to Kenton, from Dover's outskirts to the northern Kent County farmland — we serve all of central Delaware's residential and agricultural communities.",
  nearbyCitiesIntro: "We serve Smyrna and nearby communities including Clayton, Kenton, Hartly, Cheswold, Leipsic, Camden, Wyoming, Dover, Middletown, and Townsend. We also serve northern Sussex County residents from Milford and Frederica. If you are anywhere between Dover and Middletown, we have you covered.",
  howItWorksIntro: "Most Smyrna travelers complete everything online and ship documents overnight. No need to drive 70–80 minutes to Philadelphia or take time off from work.",
  mistakesIntro: "Smyrna, Delaware has a unique mix of rural families, commuters on DE Route 1, and small-town residents. Here are the most common passport mistakes we see from central Delaware travelers.",
  testimonialsIntro: "Smyrna, Delaware travelers — from farming families to commuters — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Smyrna, DE? Here are the most common ones we hear from central Delaware travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  dmvWebsite: "https://www.dmv.de.gov",
  dmvLabel: "Delaware DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 70–80 min to Philadelphia", "Full day commitment — challenging for commuters"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for family and commuter travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Smyrna travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute family trips and business travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Family travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Smyrna adults and commuters with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Smyrna address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Smyrna residents and families — including first international trips.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Smyrna and central Delaware residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Smyrna families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Smyrna Delaware residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Smyrna DE", description: "Urgent passport processing for Smyrna Delaware travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Smyrna DE", description: "Fast passport renewal for Smyrna Delaware residents and commuters with a previously-issued passport." },
    { name: "First-Time Passport Smyrna DE", description: "New passport application services for Smyrna Delaware residents applying for their first passport." },
    { name: "Child Passport Smyrna DE", description: "Minor passport services for Smyrna Delaware families with children under 16." },
  ],
  testimonials: [
    {
      name: "Jennifer W.",
      location: "Smyrna, DE",
      rating: 5,
      text: "Needed passports for our family cruise with only two weeks to go. Fast Passport Center had all four processed in 5 days. As a busy mom in central Delaware, I cannot thank them enough.",
      service: "Family Passport Bundle",
    },
    {
      name: "Mark D.",
      location: "Clayton, DE",
      rating: 5,
      text: "Business trip to Toronto with six days notice. They reviewed my renewal application, caught a small error, and delivered my passport in 3 days. Clayton residents need this service.",
      service: "Passport Renewal",
    },
    {
      name: "Catherine L.",
      location: "Smyrna, DE",
      rating: 5,
      text: "First-time passport for my daughter's school trip to France. Fast Passport Center helped us find the closest acceptance facility and handled everything. Delivered in 6 days.",
      service: "Child Passport",
    },
    {
      name: "Robert H.",
      location: "Kenton, DE",
      rating: 5,
      text: "Lost my passport before a vacation to Jamaica. They handled the emergency replacement, the DS-64, and everything else. Had it in 48 hours. Amazing for a rural Kent County resident.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "smyrna-de-1",
      name: "Smyrna Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 S Main St",
      city: "Smyrna",
      state: "DE",
      zip: "19977",
      phone: "(302) 653-3711",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Smyrna on DE Route 1",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Smyrna and northern Kent County. Appointments required — book 1–2 weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "smyrna-de-2",
      name: "Clayton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "20 E Main St",
      city: "Clayton",
      state: "DE",
      zip: "19938",
      phone: "(302) 653-9451",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 3 miles west of Smyrna",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western Smyrna and Clayton residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "smyrna-de-3",
      name: "Dover Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1350 Forrest Ave",
      city: "Dover",
      state: "DE",
      zip: "19904",
      phone: "(302) 678-8721",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 12 miles south of Smyrna",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for southern Kent County residents closer to Dover.",
      icon: "ri-mail-line",
    },
    {
      id: "smyrna-de-4",
      name: "Kenton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "28 E Commerce St",
      city: "Kenton",
      state: "DE",
      zip: "19955",
      phone: "(302) 653-6341",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 8 miles west of Smyrna",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Rural facility for western Kent County residents. Limited hours — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "smyrna-de-5",
      name: "Kent County Clerk of Peace",
      type: "County Clerk Passport Acceptance",
      address: "414 Federal St",
      city: "Dover",
      state: "DE",
      zip: "19901",
      phone: "(302) 744-2381",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Approximately 12 miles south of Smyrna in Dover",
      website: "https://www.kentcountyde.gov/",
      websiteLabel: "Kent County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to rural post offices. Good for Kent County residents.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 70–80 min drive to Philadelphia", fpc: "No — ship from Smyrna" },
    { feature: "Rural Area Support", agency: "No flexibility for rural residents", fpc: "Yes — we serve all of Kent County" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Smyrna's rural families and commuters often discover their passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Small-town photo services sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Smyrna and Kent County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Knowing the Closest Acceptance Facility",
      description: "Many central Delaware residents assume they need to go to Dover or Wilmington for passport acceptance. The Smyrna Post Office is your primary local option.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Smyrna residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Smyrna or Clayton." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near Smyrna or Dover." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Smyrna address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Smyrna, DE?", a: "Fast Passport Center offers emergency passport processing in Smyrna, Delaware in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (70–80 minutes from Smyrna) — without you needing to make the drive." },
    { q: "Does Smyrna, Delaware have its own passport agency?", a: "No — neither Smyrna nor the state of Delaware has a regional passport agency. The closest facility is the Philadelphia Passport Agency, approximately 70–80 minutes from Smyrna via DE Route 1 and I-95. Fast Passport Center's courier service means Smyrna residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Clayton or Kenton?", a: "Absolutely. Fast Passport Center serves all of central Kent County including Clayton, Kenton, Hartly, Cheswold, and Leipsic. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Smyrna, DE?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Smyrna, Delaware travelers use?", a: "Most Smyrna residents fly internationally through Philadelphia International Airport (PHL), approximately 70–80 minutes north via DE Route 1 and I-95. Wilmington Airport (ILG) in New Castle offers limited domestic service at about 35 minutes north. Baltimore-Washington International (BWI) is an alternative for some travelers. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Smyrna?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Smyrna, Clayton, and Kenton. Whether you need child passports, renewals, or first-time applications, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to Smyrna?", a: "The Smyrna Post Office at 101 S Main St is the primary local acceptance facility. The Clayton Post Office at 20 E Main St is also nearby for western residents. For county-level services, the Kent County Clerk of Peace in Dover at 414 Federal St offers extended hours. All require appointments." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Central Delaware residents should plan ahead, especially for family trips and seasonal travel." },
  ],
  photoLocations: [
    { name: "Walgreens — Smyrna", address: "200 N Dupont Blvd, Smyrna, DE 19977", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Smyrna", address: "100 S Carter Rd, Smyrna, DE 19977", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Smyrna", address: "500 N Dupont Blvd, Smyrna, DE 19977", hours: "Mon–Sun: 6AM–11PM" },
    { name: "UPS Store — Smyrna", address: "300 N Dupont Blvd, Smyrna, DE 19977", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "DMV — Dover", address: "3030 S Dupont Hwy, Dover, DE 19901", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves all of Kent County. REAL ID processing available.", featured: true },
    { name: "DMV — Middletown", address: "1099 S Dupont Hwy, Middletown, DE 19709", hours: "Mon–Fri: 8AM–4:30PM", note: "Alternative for northern Kent County residents. Appointments recommended.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "70–80 miles from Smyrna, DE",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for Smyrna travelers. 70–80 minute drive north on DE Route 1 and I-95. Major international hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "35 miles from Smyrna, DE",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Closest airport to Smyrna with limited domestic service. No major international connections.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Airport",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "60–70 miles from Smyrna, DE",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Alternative option for budget airlines and travelers with flexible routing.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
  ],
};